import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, HelpCircle, Loader2 } from 'lucide-react';
import { useAssistantRuntime } from '@assistant-ui/react';
import {
  Button,
  MarkdownContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui';
import { useAnswerAskUser, useCancelAskUser, useInboxItem } from '@/hooks/useInbox';
import { hasOpenConnection } from '@/utils/direct-run-connection';
import { formatAskUserAnswers } from '@/utils/ask-user';
import { verifyAskUserResolution } from '@/utils/ask-user-verify';
import {
  AskUserQuestionField,
  isAnswerComplete,
} from '@/components/inbox/AskUserQuestionField';
import type {
  AskUserAnswer,
  AskUserQuestion,
  AskUserRequestStatus,
} from '@/types/inbox';

/** The synthesized `__ask_user` tool-call part's `input` payload. */
export interface InlineAskUserArgs {
  askUserRequestId?: string;
  inboxItemId?: string;
  spaceId?: string;
  conversationId?: string;
  questions?: AskUserQuestion[];
  status?: AskUserRequestStatus;
  answers?: AskUserAnswer[];
}

interface InlineAskUserCardProps {
  args: InlineAskUserArgs;
  /** Rendered attached above the composer — shows keyboard hints, tighter chrome. */
  attached?: boolean;
  /** Called after the request is answered/dismissed (host removes the card). */
  onResolved?: () => void;
  /** Preview/test override — used instead of the answer mutation when provided. */
  onAnswerOverride?: (answers: AskUserAnswer[]) => Promise<void>;
  /** Preview/test override — used instead of the cancel mutation when provided. */
  onCancelOverride?: (reason?: string) => Promise<void>;
}

/**
 * Renders an agent `ask_user` (HITL) request as an interactive card. Mounted by
 * `ComposerAskUserPanel` directly above the message input while a question is
 * pending. Reuses the inbox form primitives (`AskUserQuestionField`) and the
 * same answer/cancel mutations, so answering keeps the sidebar inbox in sync.
 * Supports every question type (text/select/multi_select/confirm) plus a
 * streamlined approve/dismiss flow for `review` questions.
 */
export function InlineAskUserCard({
  args,
  attached,
  onResolved,
  onAnswerOverride,
  onCancelOverride,
}: InlineAskUserCardProps) {
  const toast = useToast();
  const answer = useAnswerAskUser({ status: 'pending' });
  const cancel = useCancelAskUser({ status: 'pending' });
  // The live conversation thread's runtime — used to answer over the live
  // socket instead of REST when one is open for this conversation. NOTE: in
  // @assistant-ui 0.12 `{ optional: true }` still throws "requires an
  // AuiProvider" when rendered with no runtime (the optional check runs after
  // dereferencing the client), so this card must always be hosted inside an
  // AssistantRuntimeProvider — the dev showcase supplies a no-op one.
  const runtime = useAssistantRuntime({ optional: true });

  const askUserRequestId = args.askUserRequestId ?? '';
  const inboxItemId = args.inboxItemId ?? '';
  const questions = useMemo(() => args.questions ?? [], [args.questions]);
  const isReview =
    questions.length > 0 && questions.every((q) => q.inputType === 'review');
  const isConfirm =
    questions.length === 1 && questions[0].inputType === 'confirm';

  // Authoritative status + spaceId from the inbox source, used to converge when
  // the request is answered/cancelled in another tab or after the run ended.
  // Skipped in preview (no inboxItemId / override handlers present).
  const inboxItem = useInboxItem(inboxItemId, {
    expand: 'source',
    enabled: !!inboxItemId && !onAnswerOverride,
  });

  const [localStatus, setLocalStatus] = useState<AskUserRequestStatus | null>(
    null,
  );
  const effectiveStatus: AskUserRequestStatus =
    localStatus ??
    inboxItem.data?.source?.status ??
    args.status ??
    'pending';
  const isPending = effectiveStatus === 'pending';

  const spaceId = args.spaceId ?? inboxItem.data?.spaceId ?? undefined;
  const canAnswer = !!onAnswerOverride || !!spaceId;
  const isSubmitting = answer.isPending || cancel.isPending;

  const [answers, setAnswers] = useState<Record<string, AskUserAnswer>>({});
  const [activeTab, setActiveTab] = useState<string | null>(null);
  // Which confirm choice is in flight, so the spinner shows on the clicked
  // button (Yes vs No) rather than always on Yes.
  const [confirmChoice, setConfirmChoice] = useState<'yes' | 'no' | null>(null);

  // Reset draft answers if the underlying request changes.
  useEffect(() => {
    setAnswers({});
    setActiveTab(null);
  }, [askUserRequestId]);

  function setQuestionAnswer(next: AskUserAnswer) {
    setAnswers((prev) => ({ ...prev, [next.questionId]: next }));
  }

  const allComplete = questions.every((q) =>
    isAnswerComplete(answers[q.questionId]?.value),
  );

  async function runAnswer(payload: AskUserAnswer[], successMsg: string) {
    try {
      if (onAnswerOverride) {
        await onAnswerOverride(payload);
      } else if (!spaceId || !askUserRequestId) {
        return;
      } else if (runtime && args.conversationId && hasOpenConnection(args.conversationId)) {
        // Live runtime socket open → answer over it so the turn resumes on THIS
        // session's warm micro-VM. The rendered answer becomes the user bubble;
        // the structured answers ride on `runConfig.custom` → the adapter forwards
        // them on the prompt frame. The backend closeout is SUPPOSED to resolve
        // the request + inbox item (emits `ask_user_answered` +
        // `inbox.item_resolved`) and continue the turn — so we skip REST
        // `/answer` here (sending both would dispatch the run twice). But the
        // closeout fails silently in several backend paths, leaving the request
        // `pending` forever — the verifier below polls and falls back to REST
        // `/answer` with `continueOverWs` (resolution only, no dispatch).
        runtime.thread.append({
          role: 'user',
          content: [{ type: 'text', text: formatAskUserAnswers(questions, payload) }],
          runConfig: { custom: { askUserRequestId, answers: payload } },
          // Render the optimistic echo as the structured "Answered" block. The
          // backend dedups its own `conversation.message_added` for this run, so
          // we build the ref locally from the data we already hold.
          metadata: {
            custom: {
              askUserRef: {
                askUserRequestId,
                inboxItemId,
                kind: 'answer' as const,
                snapshot: { questions, answers: payload },
              },
            },
          },
        });
        if (inboxItemId) {
          void verifyAskUserResolution({
            spaceId,
            askUserRequestId,
            inboxItemId,
            answers: payload,
          });
        }
      } else {
        // No live socket (answered from the inbox elsewhere / socket dropped) →
        // REST dispatch as before; the backend pins the follow-up run to this
        // conversation's micro-VM.
        await answer.mutateAsync({
          spaceId,
          requestId: askUserRequestId,
          inboxItemId,
          answers: payload,
        });
      }
      toast.success(successMsg);
      // When hosted in the composer panel, let it fade the card out (keep the
      // current content during the exit). Standalone (dev) shows the banner.
      if (onResolved) onResolved();
      else setLocalStatus('answered');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send answer');
    }
  }

  async function submit() {
    const payload = questions.map((q) => {
      const a = answers[q.questionId];
      return {
        questionId: q.questionId,
        value: a?.value ?? '',
        ...(a?.comment ? { comment: a.comment } : {}),
      };
    });
    await runAnswer(payload, 'Answer sent');
  }

  async function answerConfirm(value: 'yes' | 'no') {
    setConfirmChoice(value);
    try {
      await runAnswer(
        [{ questionId: questions[0].questionId, value }],
        'Answer sent',
      );
    } finally {
      setConfirmChoice(null);
    }
  }

  async function approve() {
    const payload: AskUserAnswer[] =
      questions.length > 0
        ? questions.map((q) => ({ questionId: q.questionId, value: 'approved' }))
        : [{ questionId: 'review', value: 'approved' }];
    await runAnswer(payload, 'Approved');
  }

  async function dismiss() {
    // Discard: fade the card out immediately, then fire the cancel in the
    // background. No "dismissed" state is shown.
    onResolved?.();
    try {
      if (onCancelOverride) {
        await onCancelOverride('user_dismissed');
      } else if (spaceId && askUserRequestId) {
        await cancel.mutateAsync({
          spaceId,
          requestId: askUserRequestId,
          inboxItemId,
          reason: 'user_dismissed',
        });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not dismiss');
    }
  }

  // Nothing to ask, or the request resolved while still hosted in the composer
  // panel — render nothing rather than an empty "The agent has a question"
  // shell. (Standalone/dev usage keeps the answered banner.)
  if (questions.length === 0) return null;
  if (!isPending && onResolved) return null;

  return (
    <div
      data-ask-user-request-id={askUserRequestId}
      className={
        'overflow-hidden border border-primary/30 bg-card shadow-2xl shadow-black/40 ' +
        // Match the composer: full width + same (rounded-3xl) corner radius.
        (attached ? 'mb-2 w-full rounded-3xl' : 'my-3 rounded-lg')
      }
    >
      <div className="flex items-center gap-2 border-b border-border/60 bg-primary/5 px-4 py-2.5">
        <HelpCircle className="h-4 w-4 shrink-0 text-primary" />
        <span className="text-sm font-medium text-foreground">
          {isReview ? 'The agent needs your review' : 'The agent has a question'}
        </span>
      </div>

      <div className="px-4 py-4">
        {!isPending ? (
          <ResolvedBanner status={effectiveStatus} />
        ) : isReview ? (
          <ReviewBody
            questions={questions}
            canAnswer={canAnswer}
            isSubmitting={isSubmitting}
            answerPending={answer.isPending}
            onApprove={approve}
            onDismiss={dismiss}
          />
        ) : (
          <div className="space-y-4">
            {questions.length === 1 ? (
              <AskUserQuestionField
                question={questions[0]}
                answer={answers[questions[0].questionId]}
                onChange={setQuestionAnswer}
                disabled={!canAnswer || isSubmitting}
                suppressConfirmInput={isConfirm}
              />
            ) : (
              <Tabs
                value={activeTab ?? questions[0]?.questionId ?? ''}
                onValueChange={setActiveTab}
              >
                <TabsList className="h-auto w-full justify-start rounded-none border-b bg-transparent p-0">
                  {questions.map((q, idx) => (
                    <TabsTrigger
                      key={q.questionId}
                      value={q.questionId}
                      className="relative rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
                    >
                      {q.header || `Q${idx + 1}`}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {questions.map((q) => (
                  <TabsContent key={q.questionId} value={q.questionId} className="mt-6">
                    <AskUserQuestionField
                      question={q}
                      answer={answers[q.questionId]}
                      onChange={setQuestionAnswer}
                      disabled={!canAnswer || isSubmitting}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}

            {!canAnswer && (
              <p className="text-xs text-muted-foreground">
                This question can't be answered here — open it from your inbox.
              </p>
            )}

            {isConfirm ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={dismiss}
                  disabled={!canAnswer || isSubmitting}
                >
                  Dismiss
                </Button>
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => answerConfirm('no')}
                    disabled={!canAnswer || isSubmitting}
                  >
                    {confirmChoice === 'no' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    No
                  </Button>
                  <Button
                    onClick={() => answerConfirm('yes')}
                    disabled={!canAnswer || isSubmitting}
                  >
                    {confirmChoice === 'yes' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Yes
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={dismiss} disabled={!canAnswer || isSubmitting}>
                  Dismiss
                </Button>
                <Button onClick={submit} disabled={!canAnswer || !allComplete || isSubmitting}>
                  {answer.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Answer
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewBody({
  questions,
  canAnswer,
  isSubmitting,
  answerPending,
  onApprove,
  onDismiss,
}: {
  questions: AskUserQuestion[];
  canAnswer: boolean;
  isSubmitting: boolean;
  answerPending: boolean;
  onApprove: () => void;
  onDismiss: () => void;
}) {
  const body = questions[0]?.question;
  return (
    <div className="space-y-4">
      {body && <ClampedMarkdown>{body}</ClampedMarkdown>}
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDismiss} disabled={!canAnswer || isSubmitting}>
          Dismiss
        </Button>
        <Button size="sm" onClick={onApprove} disabled={!canAnswer || isSubmitting}>
          {answerPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
          )}
          Approve
        </Button>
      </div>
    </div>
  );
}

/**
 * Renders markdown that may be long (e.g. a review write-up) without letting the
 * card grow unbounded in the conversation. Clamps to `maxHeight` with a fade and
 * a Show more / Show less toggle; only shows the toggle when content overflows.
 */
function ClampedMarkdown({
  children,
  maxHeight = 240,
}: {
  children: string;
  maxHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setOverflowing(el.scrollHeight > maxHeight + 8);
    measure();
    // Markdown sub-content (code blocks, etc.) can settle after first paint.
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, [children, maxHeight]);

  return (
    <div>
      <div className="relative">
        <div
          ref={ref}
          className="overflow-hidden"
          style={{ maxHeight: expanded ? undefined : maxHeight }}
        >
          <MarkdownContent size="sm" className="text-muted-foreground">
            {children}
          </MarkdownContent>
        </div>
        {overflowing && !expanded && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-card to-transparent" />
        )}
      </div>
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="focus-ring mt-1 rounded text-xs font-medium text-primary hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function ResolvedBanner({ status }: { status: AskUserRequestStatus }) {
  // Only "answered" shows a trace; dismissed/cancelled cards just fade out.
  if (status !== 'answered') return null;
  return (
    <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
      <CheckCircle2 className="h-4 w-4 shrink-0" />
      Answered
    </div>
  );
}
